import { useState, useEffect } from 'react';
import { Common } from '../../utils/common';
import moment from 'moment';

/**
 * @typedef {Object} Filters - Filters state which will be saved in local storage
 * @property {string} start_date - Start date in YYYYMMDD format
 * @property {string} end_date - End date in YYYYMMDD format
 * @property {{ value: number; className: string }[]} domains - Array of domain objects from the domains dropdown
 * @property {boolean} show_inactive - Whether or not to show inactive groups and locations
 */

/**
 * Custom hook that keeps filters state in sync with local storage
 * @param {Object} [defaultFilterState={}] - Optional default state for reports with additional filters
 * @param {Object} [defaultFiltersFromURL={}] - Optional default state for reports with filters from the URL
 * @param {string} [domainId] - Optional domainId for reports in CRM mode
 * @param {string} [maxDate] - Optional maxDate for reports with a maxDate filter in YYYYMMDD format
 * @returns {[Filters, import('react').Dispatch<import('react').SetStateAction<Filters>>]}
 */
export default function useFiltersState(
  defaultFilterState = {},
  defaultFiltersFromURL = {},
  domainId,
  maxDate
) {
  const [filters, setFilters] = useState(() => {
    // Initialize the filters state
    const defaultStartDate = moment().format('YYYYMMDD');
    const defaultEndDate = maxDate ?? moment().format('YYYYMMDD');
    const defaultShowInactive = false;

    // If in CRM mode, initialize filters with the default states
    if (Common.in_crm_mode()) {
      return {
        ...defaultFilterState,
        ...defaultFiltersFromURL,
        start_date: defaultStartDate,
        end_date: defaultEndDate,
        domains: [{ value: Number(domainId), className: 'filter-location' }],
      };
    } else {
      // If not in CRM mode, initialize filters with the filters in local storage
      const filtersInLocalStorage = window.localStorage.getItem('filters');
      if (filtersInLocalStorage) {
        const parsedFilters = JSON.parse(filtersInLocalStorage);

        // Only adds the properties from localStorage that exist in the defaultFilterState
        // so filters saved from other reports don't get added to the current report
        for (const property in parsedFilters) {
          if (
            // Filter properties in every report
            property !== 'domains' &&
            property !== 'start_date' &&
            property !== 'end_date' &&
            property !== 'show_inactive' &&
            // Filter properties only in current report defaultFilterState
            !defaultFilterState.hasOwnProperty(property)
          ) {
            delete parsedFilters[property];
          }
        }

        // If filters.end_date is after the maxDate, set it to the maxDate
        if (moment(parsedFilters.end_date).isAfter(maxDate)) {
          parsedFilters.start_date = maxDate;
          parsedFilters.end_date = maxDate;
        }

        // Filters from url override filters from local storage
        return {
          ...defaultFilterState,
          ...parsedFilters,
          ...defaultFiltersFromURL,
        };
      } else {
        // If no filters in local storage, initialize filters with the default states
        return {
          ...defaultFilterState,
          ...defaultFiltersFromURL,
          start_date: defaultStartDate,
          end_date: defaultEndDate,
          show_inactive: defaultShowInactive,
        };
      }
    }
  });

  // Updates local storage filters any time the user changes filters
  useEffect(() => {
    if (Common.in_crm_mode()) {
      return;
    }
    const filtersInLocalStorage = JSON.parse(window.localStorage.getItem('filters'));
    // Only overrides existing filters, rather than replacing them entirely
    // This is so reports with different filters won't be affected by changes in another report
    const newFilters = { ...filtersInLocalStorage, ...filters };
    window.localStorage.setItem('filters', JSON.stringify(newFilters));
  }, [filters]);

  return [filters, setFilters];
}
